import { ComponentProps, FC, useEffect, useRef, useState } from 'react';

import { Link } from '@inertiajs/react';
import { clsx } from 'clsx';
import { Minus, Plus, ShoppingCart, Sparkles } from 'lucide-react';

import { OfferPhotoGallery } from '../../components/shared/OfferPhotoGallery';
import { OfferSellerSummary } from '../../components/shared/OfferSellerSummary';
import { ProductCardBadges } from '../../components/shared/ProductCardBadges';
import { WishlistButton } from '../../components/shared/WishlistButton';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { Image } from '../../components/ui/Image';
import { Skeleton } from '../../components/ui/Skeleton';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '../../components/ui/Tooltip';
import { useCart } from '../../context/cart-context';
import { useOfferViewTracking } from '../../hooks/useOfferViewTracking';
import { OrdersService } from '../../services/OrdersService';
import { Offer, OfferProperty } from '../../types';
import { OfferQuantityInput } from '../Products/OfferQuantityInput';

interface Props {
    readonly offer: Offer;
    readonly offerProperties: OfferProperty[];
    readonly loading: boolean;
    readonly eagerLoadImage: boolean;
    readonly highlightPremium?: boolean;
    readonly showPremiumBadge?: boolean;
    readonly showSellerInfo?: boolean;
}

interface OfferMetaItem {
    label: string;
    value: string;
}

const META_ROW_GAP_PX = 8;
const SELLER_META_LABEL = 'Продавець';

const PREMIUM_CARD_SHADOW_CLASS =
    'shadow-[0_0_22px_rgba(245,158,11,0.16),0_0_10px_rgba(250,204,21,0.12)] sm:group-hover:shadow-[0_0_32px_rgba(245,158,11,0.22),0_0_16px_rgba(250,204,21,0.16)]';

const PREMIUM_DETAILS_SHADOW_CLASS =
    'shadow-[0_18px_34px_-12px_rgba(245,158,11,0.24),0_8px_18px_-8px_rgba(250,204,21,0.18),14px_10px_22px_-16px_rgba(245,158,11,0.18),-14px_10px_22px_-16px_rgba(245,158,11,0.18)]';

const LANGUAGE_VALUE_MAP: Record<string, string> = {
    en: 'English',
    english: 'English',
    jp: 'Japanese',
    ja: 'Japanese',
    japanese: 'Japanese',
    uk: 'Ukrainian',
    ua: 'Ukrainian',
    ukrainian: 'Ukrainian',
};

const titleize = (value: string) =>
    value
        .trim()
        .replace(/\s+/g, ' ')
        .split(/[_-]/)
        .map((chunk) =>
            chunk
                ? `${chunk[0].toUpperCase()}${chunk.slice(1).toLowerCase()}`
                : chunk,
        )
        .join(' ');

const formatLanguage = (value?: string | null) => {
    const normalizedValue = value?.trim();

    if (!normalizedValue) {
        return null;
    }

    const mappedValue = LANGUAGE_VALUE_MAP[normalizedValue.toLowerCase()];

    if (mappedValue) {
        return mappedValue;
    }

    if (
        normalizedValue === normalizedValue.toUpperCase() &&
        normalizedValue.length <= 4
    ) {
        return normalizedValue;
    }

    return titleize(normalizedValue);
};

const getOfferMeta = (
    offer: Offer,
    offerProperties: OfferProperty[],
    availableQuantity: number,
) => {
    const productOfferProperties = offer.product.product_type_id
        ? offerProperties.filter(
              (property) =>
                  property.product_type_id === offer.product.product_type_id,
          )
        : offerProperties;
    const meta: OfferMetaItem[] = OrdersService.getOfferPropertyEntries(
        offer.property_set,
        productOfferProperties,
    ).map(({ key, value }) => ({
        label: key,
        value,
    }));
    const propertyKeys = new Set(
        Object.keys(offer.property_set ?? {}).map((key) =>
            key.trim().toLowerCase(),
        ),
    );
    const language = formatLanguage(offer.product.language);

    if (language && !propertyKeys.has('language')) {
        meta.push({ label: 'Мова', value: language });
    }

    meta.push({ label: 'Доступно', value: `${availableQuantity} шт.` });

    return meta;
};

const OfferMetaRow: FC<{ item: OfferMetaItem }> = ({ item }) => {
    const rowRef = useRef<HTMLDivElement>(null);
    const labelMeasureRef = useRef<HTMLSpanElement>(null);
    const valueMeasureRef = useRef<HTMLSpanElement>(null);
    const [fitsInline, setFitsInline] = useState(false);

    useEffect(() => {
        const row = rowRef.current;
        const labelMeasure = labelMeasureRef.current;
        const valueMeasure = valueMeasureRef.current;

        if (!row || !labelMeasure || !valueMeasure) return;

        const measure = () => {
            const styles = window.getComputedStyle(row);
            const contentWidth =
                row.clientWidth -
                Number.parseFloat(styles.paddingLeft) -
                Number.parseFloat(styles.paddingRight);
            const requiredWidth =
                labelMeasure.offsetWidth +
                valueMeasure.offsetWidth +
                META_ROW_GAP_PX;

            setFitsInline(requiredWidth <= contentWidth);
        };

        measure();

        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(row);

        return () => resizeObserver.disconnect();
    }, [item.label, item.value]);

    return (
        <div
            ref={rowRef}
            className={clsx(
                'relative max-w-full min-w-0 overflow-hidden rounded-xl border border-border/60 bg-background px-2.5 py-2 text-xs dark:bg-card',
                fitsInline
                    ? 'flex items-baseline gap-2'
                    : 'flex flex-col items-stretch gap-1',
            )}
        >
            <span
                ref={labelMeasureRef}
                className="invisible fixed top-0 -left-[9999px] max-w-none whitespace-nowrap text-muted-foreground"
                aria-hidden="true"
            >
                {item.label}
            </span>
            <span
                ref={valueMeasureRef}
                className="invisible fixed top-0 -left-[9999px] max-w-none font-medium whitespace-nowrap"
                aria-hidden="true"
            >
                {item.value}
            </span>

            <span className="max-w-full shrink-0 break-words text-muted-foreground">
                {item.label}
            </span>
            <span
                className={clsx(
                    'max-w-full min-w-0 text-left font-medium [overflow-wrap:anywhere] break-words text-foreground',
                    fitsInline && 'ml-auto',
                )}
            >
                {item.value}
            </span>
        </div>
    );
};

const SellerMetaRow: FC<{
    offer: Offer;
    onSellerClick: NonNullable<
        ComponentProps<typeof OfferSellerSummary>['onSellerClick']
    >;
}> = ({ offer, onSellerClick }) => {
    const rowRef = useRef<HTMLDivElement>(null);
    const labelMeasureRef = useRef<HTMLSpanElement>(null);
    const contentMeasureRef = useRef<HTMLDivElement>(null);
    const [fitsInline, setFitsInline] = useState(false);

    useEffect(() => {
        const row = rowRef.current;
        const labelMeasure = labelMeasureRef.current;
        const contentMeasure = contentMeasureRef.current;

        if (!row || !labelMeasure || !contentMeasure) return;

        const measure = () => {
            const styles = window.getComputedStyle(row);
            const contentWidth =
                row.clientWidth -
                Number.parseFloat(styles.paddingLeft) -
                Number.parseFloat(styles.paddingRight);
            const requiredWidth =
                labelMeasure.offsetWidth +
                contentMeasure.offsetWidth +
                META_ROW_GAP_PX;

            setFitsInline(requiredWidth <= contentWidth);
        };

        measure();

        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(row);

        return () => resizeObserver.disconnect();
    }, [
        offer.seller_completed_orders_count,
        offer.seller_name,
        offer.user.avatar,
        offer.user.nickname,
        offer.user.url_path,
    ]);

    return (
        <div
            ref={rowRef}
            className={clsx(
                'relative max-w-full min-w-0 overflow-hidden rounded-xl border border-border/60 bg-background px-2.5 py-2 text-xs dark:bg-card',
                fitsInline
                    ? 'flex items-center gap-2'
                    : 'flex flex-col items-stretch gap-1',
            )}
            data-testid={`seller-offer-card-seller-row-${offer.id}`}
        >
            <span
                ref={labelMeasureRef}
                className="invisible fixed top-0 -left-[9999px] max-w-none whitespace-nowrap text-muted-foreground"
                aria-hidden="true"
            >
                {SELLER_META_LABEL}
            </span>
            <div
                ref={contentMeasureRef}
                className="invisible fixed top-0 -left-[9999px] max-w-none"
                aria-hidden="true"
            >
                <OfferSellerSummary
                    sellerName={offer.seller_name}
                    sellerNickname={offer.user.nickname}
                    sellerPath={offer.user.url_path}
                    sellerAvatar={offer.user.avatar}
                    completedSalesCount={offer.seller_completed_orders_count}
                    className="max-w-none"
                />
            </div>

            <span className="max-w-full shrink-0 break-words text-muted-foreground">
                {SELLER_META_LABEL}
            </span>
            <OfferSellerSummary
                sellerName={offer.seller_name}
                sellerNickname={offer.user.nickname}
                sellerPath={offer.user.url_path}
                sellerAvatar={offer.user.avatar}
                completedSalesCount={offer.seller_completed_orders_count}
                onSellerClick={onSellerClick}
                className={clsx(
                    'max-w-full min-w-0 overflow-hidden',
                    fitsInline && 'ml-auto',
                )}
                contentClassName="max-w-full min-w-0 justify-start overflow-hidden"
            />
        </div>
    );
};

export const SellerOfferProductCard: FC<Props> = ({
    offer,
    offerProperties,
    loading,
    eagerLoadImage,
    highlightPremium = false,
    showPremiumBadge = true,
    showSellerInfo = false,
}) => {
    const {
        cartItems,
        canAddQuantity,
        canAddToCart,
        getOfferQuantity,
        getTooltipMessage,
        handleAddToCart,
        isOwnOffer,
        updateOfferQuantity,
    } = useCart();
    const [isMobileOverlayOpen, setIsMobileOverlayOpen] = useState(false);
    const observeOfferView = useOfferViewTracking<HTMLDivElement>();

    const selectedQuantity = getOfferQuantity(offer.id);
    const existingItem = cartItems.find((item) => item.id === offer.id);
    const availableQuantity = Math.max(
        offer.amount - (existingItem?.quantity ?? 0),
        0,
    );
    const maxSelectableQuantity = Math.max(1, availableQuantity);
    const isInCart = Boolean(existingItem?.quantity);
    const fullyAddedToCart = isInCart && availableQuantity <= 0;
    const ownOffer = isOwnOffer(offer);
    const sellerUnavailable = Boolean(
        !ownOffer && (offer.seller_paused || offer.user.seller_paused),
    );
    const soldOut = availableQuantity <= 0;
    const disabled =
        ownOffer ||
        sellerUnavailable ||
        soldOut ||
        !canAddToCart(offer) ||
        !canAddQuantity(offer, selectedQuantity);
    const tooltipMessage = ownOffer
        ? getTooltipMessage(offer)
        : fullyAddedToCart
          ? 'Максимальна кількість цієї пропозиції вже у кошику'
          : soldOut
            ? 'Ця пропозиція зараз недоступна'
            : getTooltipMessage(offer);
    const cartButtonLabel = ownOffer
        ? 'Ваша пропозиція'
        : sellerUnavailable
          ? 'Недоступно'
          : fullyAddedToCart
            ? 'У кошику'
            : soldOut
              ? 'Немає'
              : 'В кошик';
    const offerMeta = getOfferMeta(offer, offerProperties, availableQuantity);
    const cardProduct = {
        ...offer.product,
        price: offer.price,
    };

    useEffect(() => {
        if (selectedQuantity > maxSelectableQuantity) {
            updateOfferQuantity(offer.id, maxSelectableQuantity);
        }
    }, [
        maxSelectableQuantity,
        offer.id,
        selectedQuantity,
        updateOfferQuantity,
    ]);

    const sellerMetaRow = showSellerInfo ? (
        <SellerMetaRow
            offer={offer}
            onSellerClick={(event) => event.stopPropagation()}
        />
    ) : null;

    const metaPanel = (
        <div className="grid max-w-full min-w-0 gap-1.5 overflow-hidden">
            {sellerMetaRow}
            {offerMeta.map((item) => (
                <OfferMetaRow key={`${offer.id}-${item.label}`} item={item} />
            ))}
        </div>
    );

    const renderControlsPanel = (isMobile = false) => (
        <div
            className="flex items-center gap-1"
            onClick={(event) => event.stopPropagation()}
        >
            <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-6 w-6 bg-transparent"
                onClick={() =>
                    updateOfferQuantity(offer.id, selectedQuantity - 1)
                }
                disabled={
                    ownOffer || sellerUnavailable || selectedQuantity <= 1
                }
            >
                <Minus className="h-3 w-3" />
            </Button>

            <OfferQuantityInput
                type="number"
                min="1"
                max={maxSelectableQuantity}
                inputMode="numeric"
                disabled={ownOffer || sellerUnavailable || soldOut}
                value={Math.min(selectedQuantity, maxSelectableQuantity)}
                onChangeValue={(value) => updateOfferQuantity(offer.id, value)}
                className="h-6 w-12 px-1 text-center text-xs dark:bg-card [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />

            <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-6 w-6 bg-transparent"
                onClick={() =>
                    updateOfferQuantity(offer.id, selectedQuantity + 1)
                }
                disabled={
                    ownOffer ||
                    sellerUnavailable ||
                    soldOut ||
                    selectedQuantity >= maxSelectableQuantity
                }
            >
                <Plus className="h-3 w-3" />
            </Button>

            <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                    <div
                        className={
                            isMobile
                                ? 'ml-auto shrink-0'
                                : 'ml-auto shrink-0 xl:min-w-0 xl:flex-1'
                        }
                    >
                        <Button
                            type="button"
                            size={isMobile ? 'icon' : 'sm'}
                            variant="outline"
                            className={clsx(
                                'rounded-2xl bg-background dark:bg-card',
                                isMobile
                                    ? 'h-8 w-8'
                                    : 'h-8 w-8 px-0 text-xs xl:w-full xl:px-2',
                            )}
                            onClick={() => handleAddToCart(offer)}
                            disabled={disabled}
                            aria-label={cartButtonLabel}
                        >
                            <ShoppingCart
                                className={clsx(
                                    isMobile
                                        ? 'h-3.5 w-3.5'
                                        : 'h-3.5 w-3.5 xl:mr-1 xl:h-3 xl:w-3',
                                )}
                            />
                            {!isMobile && (
                                <span className="hidden xl:inline">
                                    {cartButtonLabel}
                                </span>
                            )}
                        </Button>
                    </div>
                </TooltipTrigger>

                {tooltipMessage && (
                    <TooltipContent side="top" align="center">
                        <p>{tooltipMessage}</p>
                    </TooltipContent>
                )}
            </Tooltip>
        </div>
    );

    return (
        <div
            ref={observeOfferView}
            data-offer-id={offer.id}
            data-testid={`seller-offer-card-${offer.id}`}
            className={clsx(
                'group relative z-0 transition-transform duration-200 sm:hover:z-20 sm:hover:scale-105',
                isMobileOverlayOpen && 'z-20 scale-105',
            )}
        >
            <Card
                className={clsx(
                    'relative overflow-hidden rounded-2xl transition-all duration-200',
                    isMobileOverlayOpen &&
                        'rounded-b-none border-b-transparent shadow-none',
                    'sm:group-hover:rounded-b-none sm:group-hover:border-b-transparent',
                    !highlightPremium && 'sm:group-hover:shadow-lg',
                    highlightPremium && PREMIUM_CARD_SHADOW_CLASS,
                )}
            >
                <button
                    type="button"
                    className="absolute inset-0 z-10 lg:hidden"
                    aria-label="Показати дані оферу"
                    aria-expanded={isMobileOverlayOpen}
                    onClick={() => setIsMobileOverlayOpen((open) => !open)}
                />

                <div
                    className={clsx('h-full w-full', { 'opacity-0': loading })}
                >
                    <div
                        className={clsx('block', {
                            'pointer-events-none': loading,
                        })}
                    >
                        <div className="relative aspect-[3/4]">
                            <Image
                                src={offer.product.image}
                                alt={offer.product.image_alt}
                                className="h-full w-full object-fill"
                                loading={eagerLoadImage ? 'eager' : 'lazy'}
                            />

                            <Link
                                href={offer.product.url_path}
                                aria-label={`Відкрити ${offer.product.name}`}
                                className="absolute inset-0 z-20 hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none lg:block"
                                data-testid={`seller-offer-product-image-link-${offer.id}`}
                            />

                            {isMobileOverlayOpen && (
                                <Link
                                    href={offer.product.url_path}
                                    aria-label={`Відкрити ${offer.product.name}`}
                                    className="absolute inset-0 z-20 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none lg:hidden"
                                    data-testid={`seller-offer-product-mobile-image-link-${offer.id}`}
                                />
                            )}

                            {!offer.product.inStock && (
                                <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                                    <Badge
                                        variant="destructive"
                                        className="text-xs sm:text-sm"
                                    >
                                        Немає в наявності
                                    </Badge>
                                </div>
                            )}
                        </div>
                    </div>

                    <CardContent className="p-2 sm:p-3">
                        <div className="flex h-20 flex-col justify-between gap-2">
                            <div className="flex min-w-0 items-start gap-1.5">
                                <h3 className="line-clamp-2 min-w-0 text-sm font-medium">
                                    <Link
                                        href={offer.product.url_path}
                                        className="relative z-20 transition-colors hover:text-primary focus-visible:text-primary focus-visible:outline-none"
                                        data-testid={`seller-offer-product-link-${offer.id}`}
                                    >
                                        {offer.product.name}
                                    </Link>
                                </h3>
                                <OfferPhotoGallery
                                    photos={offer.photos}
                                    compact
                                    className="relative z-20 h-6 w-6 shrink-0 bg-transparent shadow-none"
                                />
                            </div>

                            <div className="flex items-end justify-between gap-2">
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <ProductCardBadges
                                        product={cardProduct}
                                        hideSecondaryBadgesUntil={
                                            highlightPremium ||
                                            offer.premium_active
                                                ? 'lg'
                                                : undefined
                                        }
                                    />
                                    {showPremiumBadge &&
                                        offer.premium_active && (
                                            <Tooltip delayDuration={200}>
                                                <TooltipTrigger asChild>
                                                    <span
                                                        className="relative z-20 flex h-6 w-6 cursor-help items-center justify-center rounded-full border border-amber-300/70 bg-amber-500 text-white shadow-sm"
                                                        role="img"
                                                        aria-label="Premium пропозиція"
                                                        data-testid={`premium-offer-indicator-${offer.id}`}
                                                    >
                                                        <Sparkles className="h-3.5 w-3.5" />
                                                    </span>
                                                </TooltipTrigger>
                                                <TooltipContent
                                                    side="top"
                                                    align="center"
                                                >
                                                    <p>Premium пропозиція</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        )}
                                </div>
                                <Badge variant="outline" className="text-xs">
                                    {offer.price}₴
                                </Badge>
                            </div>
                        </div>
                    </CardContent>
                </div>

                {loading && (
                    <Skeleton
                        data-testid="product-card-loading-skeleton"
                        className="pointer-events-none absolute top-0 left-0 h-full w-full"
                    />
                )}
            </Card>

            <WishlistButton
                productId={offer.product.id}
                initialWishlisted={offer.product.wishlisted}
                className={clsx(
                    'absolute top-2 right-2 z-30 h-8 w-8',
                    isMobileOverlayOpen && 'bg-white text-slate-900',
                )}
            />

            {isMobileOverlayOpen && (
                <div
                    data-testid={`seller-offer-click-details-${offer.id}`}
                    className={clsx(
                        'absolute inset-x-0 top-full z-20 -mt-px rounded-b-2xl border border-t-0 bg-background p-3 lg:hidden dark:bg-card',
                        highlightPremium
                            ? PREMIUM_DETAILS_SHADOW_CLASS
                            : 'shadow-lg',
                    )}
                >
                    <div className="space-y-3">
                        {metaPanel}

                        <div className="pt-1">{renderControlsPanel(true)}</div>
                    </div>
                </div>
            )}

            <div
                data-testid={`seller-offer-hover-details-${offer.id}`}
                className={clsx(
                    'pointer-events-none absolute inset-x-0 top-full z-20 -mt-px hidden rounded-b-2xl border border-t-0 bg-background p-3 opacity-0 transition-opacity duration-100 dark:bg-card',
                    isMobileOverlayOpen
                        ? 'lg:block lg:group-hover:pointer-events-auto lg:group-hover:opacity-100'
                        : 'sm:block sm:group-hover:pointer-events-auto sm:group-hover:opacity-100',
                    highlightPremium
                        ? PREMIUM_DETAILS_SHADOW_CLASS
                        : 'shadow-lg',
                )}
            >
                <div className="space-y-3">
                    {metaPanel}

                    <div className="pt-1">{renderControlsPanel()}</div>
                </div>
            </div>
        </div>
    );
};
